import React, { useState } from 'react';
import { Phone, Mail, MapPin, Star, CheckCircle2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { businessInfo, services, beforeAfterProjects, benefits, reviews } from '../mock';
import BeforeAfterGallery from '../components/BeforeAfterGallery';
import ServiceCard from '../components/ServiceCard';
import ReviewCard from '../components/ReviewCard';
import WhatsAppButton from '../components/WhatsAppButton';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
    image: null
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setFormData(prev => ({ ...prev, image: file }));
      toast.success(`${file.name} pronto para enviar`);
    } else {
      toast.error("Arquivo muito grande. Por favor, envie uma imagem menor que 10MB");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('description', formData.description);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/estimate`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', phone: '', description: '', image: null });
      } else {
        throw new Error(data.detail || 'Failed to send request');
      }
    } catch (error) {
      toast.error('Error sending request', {
        description: error.message || "Failed to send estimate request. Please try again."
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-amber-50">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC222] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#FFC222] opacity-5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#FFC222] bg-opacity-10 border border-[#FFC222] px-4 py-2 rounded-full">
                <CheckCircle2 className="w-5 h-5 text-[#FFC222]" />
                <span className="text-sm font-semibold text-gray-800">Licensed & Insured Florida LLC</span>
              </div>
              
              {/* Main Headline */}
              <div>
                <h1 className="text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                  <span className="block">Trusted Construction</span>
                  <span className="block">& Repair Services in</span>
                  <span className="bg-gradient-to-r from-[#FFC222] to-[#E6AE12] bg-clip-text text-transparent">
                    Central Florida
                  </span>
                </h1>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-6 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#FFC222] rounded-full"></div>
                    <span className="text-xl font-bold text-gray-800">10 years of experience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#FFC222] rounded-full"></div>
                    <span className="text-xl font-bold text-gray-800">Over 5,000 homes served</span>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-xl text-gray-700 leading-relaxed mb-8 font-medium">
                  We deliver professional remodeling, stucco, drywall, and painting services with 
                  <span className="text-[#FFC222] font-bold"> quality</span>,
                  <span className="text-[#FFC222] font-bold"> speed</span>, and
                  <span className="text-[#FFC222] font-bold"> clean finishes</span>.
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <a href={`tel:${businessInfo.phone}`}>
                  <Button className="bg-[#FFC222] hover:bg-[#E6AE12] active:bg-[#C9960F] text-black font-bold text-lg px-10 py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                    <Phone className="mr-2" /> Call Now
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  className="border-2 border-gray-800 text-gray-900 font-bold text-lg px-10 py-7 rounded-xl hover:bg-gray-900 hover:text-white transition-all"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  View Services
                </Button>
              </div>
            </div>

            {/* Right Side - Form or Success Message */}
            <Card className="p-8 shadow-2xl border-2 border-[#FFC222] rounded-2xl bg-white backdrop-blur-sm">
              {isSubmitted ? (
                // Success Message
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent Successfully!</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Thank you for contacting us. We will respond as soon as possible.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    className="bg-[#FFC222] hover:bg-[#E6AE12] text-black font-bold px-6 py-3 rounded-lg"
                  >
                    Send Another Request
                  </Button>
                </div>
              ) : (
                // Form
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 bg-[#FFC222] rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-black" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Get a Free Estimate</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-6 font-medium">Send a photo and receive your estimate within 24 hours</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base rounded-lg border-gray-300 focus:border-[#FFC222] focus:ring-[#FFC222]"
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base rounded-lg border-gray-300 focus:border-[#FFC222] focus:ring-[#FFC222]"
                  />
                </div>
                <div>
                  <Textarea
                    name="description"
                    placeholder="Describe your project"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="text-base rounded-lg border-gray-300 focus:border-[#FFC222] focus:ring-[#FFC222]"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#FFC222] hover:bg-amber-50 transition-all bg-gray-50">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-gray-500" size={24} />
                      <span className="text-sm text-gray-600 font-medium">
                        {formData.image ? formData.image.name : 'Upload Photo (Optional, Max 10MB)'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#FFC222] hover:bg-[#E6AE12] active:bg-[#C9960F] text-black font-bold text-lg py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                >
                  Get a Free Estimate
                </Button>
              </form>
                </>
              )}
            </Card>
          </div>
        </div>
        
        {/* Google Review Button - Centered Below Hero */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex justify-center">
            <a 
              href="https://g.page/r/CfNjgZ0I7-hrEBM/review" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border-2 border-gray-200 hover:border-[#FFC222] px-6 py-4 rounded-xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 group"
            >
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FFC222] text-[#FFC222] group-hover:scale-110 transition-transform" style={{ transitionDelay: `${i * 50}ms` }} />
                ))}
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-gray-900">Love Our Work?</div>
                <div className="text-xs text-gray-600">Leave us a Google Review</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Before & <span className="text-[#FFC222]">After</span>
            </h2>
            <div className="w-24 h-1 bg-[#FFC222] mx-auto"></div>
          </div>
          <BeforeAfterGallery projects={beforeAfterProjects} />
        </div>
      </section>

      {/* Services Section */}
      <section id="contact" className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-[#FFC222]">Our</span> Services
            </h2>
            <p className="text-lg text-gray-600">Professional remodeling solutions for every need</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 fill-[#FFC222] text-[#FFC222]" />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-900">{businessInfo.rating}</span>
              <span className="text-gray-600">({businessInfo.reviewCount} reviews)</span>
            </div>
            <p className="text-lg text-gray-600 mb-8">Trusted by homeowners across Central Florida</p>
          </div>
          
          {/* Reviews Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {reviews.map(review => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
          
          <div className="text-center">
            <a 
              href={businessInfo.googleReviewsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-[#FFC222] hover:bg-[#E6AE12] text-black font-bold text-lg px-8 py-6 rounded-xl shadow-lg transition-all hover:-translate-y-1">
                View All Reviews on Google
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#FFC222] to-[#E6AE12]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-black mb-4">
            Had a Great Experience?
          </h2>
          <p className="text-xl text-gray-900 mb-8 font-medium">
            Share your experience with SAGU CONSTRUCTION and help other homeowners find quality construction services!
          </p>
          <a 
            href={businessInfo.googleReviewsUrl} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="bg-black hover:bg-gray-900 text-white font-bold text-lg px-10 py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
              <Star className="mr-2 fill-[#FFC222] text-[#FFC222]" />
              Leave a Review on Google
            </Button>
          </a>
          <p className="text-sm text-gray-800 mt-4">Your feedback means the world to us!</p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            About <span className="text-[#FFC222]">SAGU CONSTRUCTION LLC</span>
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            {businessInfo.description}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#FFC222] flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Home;
